// src/components/ui/Button.tsx
import React from "react";
import { useTranslation } from "react-i18next"
import Spinner from "./Spinner";

type Variant = "blue" | "orange" | "gray" | "ghost";

type ButtonProps = React.ButtonHTMLAttributes<HTMLButtonElement> & {
  loading?: boolean;
  /** Optional text shown while loading (e.g., "Logging in…"). If omitted, children are shown. */
  loadingText?: React.ReactNode;
  variant?: Variant;
};

const base =
  "inline-flex items-center justify-center gap-2 rounded px-4 py-2 disabled:opacity-60 disabled:pointer-events-none transition-colors";
const variants: Record<Variant, string> = {
  blue: "bg-elani-blue text-white hover:opacity-90",
  orange: "bg-elani-orange text-white hover:bg-elani-orange-dark",
  gray: "bg-gray-200 text-gray-800 hover:bg-gray-300",
  ghost: "border border-gray-300 text-gray-800 hover:bg-gray-100",
};

export default function Button({
  loading,
  loadingText,
  variant = "blue",
  className = "",
  children,
  disabled,
  ...rest
}: ButtonProps) {
  const isDisabled = loading || disabled;
  const { t } = useTranslation();
  return (
    <button
      {...rest}
      disabled={isDisabled}
      aria-busy={loading ? "true" : undefined}
      className={[base, variants[variant], className].join(" ").trim()}
    >
      {loading && <Spinner size="xs" aria-label={t("common.loading")} />}
      {loading ? (loadingText ?? children) : children}
    </button>
  );
}
